import { APlugin } from "./a-plugin";
import { EAddressField } from "../enum/e-address-field";
import {
  catchError,
  debounceTime,
  distinctUntilChanged,
  filter,
  fromEvent,
  map,
  Observable,
  switchMap,
  take,
  tap,
} from "rxjs";
import { ZipCityAutocompletionRepo } from "../../../utils/typescript/repositories/zip-city-autocompletion.repo";
import { ZipCityAutocompletionResponseModel } from "../../../utils/typescript/model/zip-city-autocompletion/zip-city-autocompletion-response.model";
import { ZipCityAutocompletionEventConfig } from "./config/zip-city-autocompletion-event.config";
import { DomService } from "../../../utils/typescript/service/dom.service";

export class ZipCityAutocompletionPlugin extends APlugin {
  protected formElement: HTMLFormElement;
  protected elements: Map<EAddressField, unknown> = new Map();
  protected documentClickEvent$: Observable<Event>;

  constructor(formElement: HTMLFormElement) {
    super();

    this.formElement = formElement;
    this.buildElementList();
    this.addEvents();
  }

  protected buildElementList(): void {
    const list = new Map();

    if (
      this.formElement.querySelector<HTMLInputElement>(
        ".address-editable-mainAddress",
      )
    ) {
      list.set(
        EAddressField.MAIN_ADDRESS_FIELD,
        this.formElement.querySelector<HTMLInputElement>(
          ".address-editable-mainAddress",
        ),
      );

      list.set(
        EAddressField.MAIN_POSTAL_CODE,
        list
          .get(EAddressField.MAIN_ADDRESS_FIELD)
          .querySelector("input[data-postalcode]"),
      );
      list.set(
        EAddressField.MAIN_CITY,
        list
          .get(EAddressField.MAIN_ADDRESS_FIELD)
          .querySelector("input[data-city]"),
      );
      list.set(
        EAddressField.MAIN_COUNTRY,
        this.formElement.querySelector("#input-field-mainAddress-countryCode"),
      );
      list.set(
        EAddressField.MAIN_SUGGESTIONS,
        list
          .get(EAddressField.MAIN_ADDRESS_FIELD)
          .querySelector("div[data-suggestions]"),
      );
      list.set(
        EAddressField.MAIN_SUGGESTION_LIST,
        list
          .get(EAddressField.MAIN_ADDRESS_FIELD)
          .querySelector(".suggestion-list"),
      );
      list.set(
        EAddressField.MAIN_CLOSE_BUTTON,
        list
          .get(EAddressField.MAIN_ADDRESS_FIELD)
          .querySelector(".suggestion-close-button"),
      );
    }

    if (
      this.formElement.querySelector<HTMLInputElement>(
        ".address-editable-deliveryAddress",
      )
    ) {
      list.set(
        EAddressField.DELIVERY_ADDRESS_FIELD,
        this.formElement.querySelector<HTMLInputElement>(
          ".address-editable-deliveryAddress",
        ),
      );

      list.set(
        EAddressField.DELIVERY_POSTAL_CODE,
        list
          .get(EAddressField.DELIVERY_ADDRESS_FIELD)
          .querySelector("input[data-postalcode]"),
      );
      list.set(
        EAddressField.DELIVERY_CITY,
        list
          .get(EAddressField.DELIVERY_ADDRESS_FIELD)
          .querySelector("input[data-city]"),
      );
      list.set(
        EAddressField.DELIVERY_COUNTRY,
        this.formElement.querySelector(
          "#input-field-deliveryAddress-countryCode",
        ),
      );
      list.set(
        EAddressField.DELIVERY_SUGGESTIONS,
        list
          .get(EAddressField.DELIVERY_ADDRESS_FIELD)
          .querySelector("div[data-suggestions]"),
      );
      list.set(
        EAddressField.DELIVERY_SUGGESTION_LIST,
        list
          .get(EAddressField.DELIVERY_ADDRESS_FIELD)
          .querySelector(".suggestion-list"),
      );
      list.set(
        EAddressField.DELIVERY_CLOSE_BUTTON,
        list
          .get(EAddressField.DELIVERY_ADDRESS_FIELD)
          .querySelector(".suggestion-close-button"),
      );
    }

    if (
      this.formElement.querySelector<HTMLInputElement>(
        ".address-editable-billingAddress",
      )
    ) {
      list.set(
        EAddressField.BILLING_ADDRESS_FIELD,
        this.formElement.querySelector<HTMLInputElement>(
          ".address-editable-billingAddress",
        ),
      );

      list.set(
        EAddressField.BILLING_POSTAL_CODE,
        list
          .get(EAddressField.BILLING_ADDRESS_FIELD)
          .querySelector("input[data-postalcode]"),
      );
      list.set(
        EAddressField.BILLING_CITY,
        list
          .get(EAddressField.BILLING_ADDRESS_FIELD)
          .querySelector("input[data-city]"),
      );
      list.set(
        EAddressField.BILLING_COUNTRY,
        this.formElement.querySelector(
          "#input-field-billingAddress-countryCode",
        ),
      );
      list.set(
        EAddressField.BILLING_SUGGESTIONS,
        list
          .get(EAddressField.BILLING_ADDRESS_FIELD)
          .querySelector("div[data-suggestions]"),
      );
      list.set(
        EAddressField.BILLING_SUGGESTION_LIST,
        list
          .get(EAddressField.BILLING_ADDRESS_FIELD)
          .querySelector(".suggestion-list"),
      );
      list.set(
        EAddressField.BILLING_CLOSE_BUTTON,
        list
          .get(EAddressField.BILLING_ADDRESS_FIELD)
          .querySelector(".suggestion-close-button"),
      );
    }

    if (
      this.formElement.querySelector<HTMLInputElement>(
        ".address-editable-payerAddress",
      )
    ) {
      list.set(
        EAddressField.PAYER_ADDRESS_FIELD,
        this.formElement.querySelector<HTMLInputElement>(
          ".address-editable-payerAddress",
        ),
      );

      list.set(
        EAddressField.PAYER_POSTAL_CODE,
        list
          .get(EAddressField.PAYER_ADDRESS_FIELD)
          .querySelector("input[data-postalcode]"),
      );
      list.set(
        EAddressField.PAYER_CITY,
        list
          .get(EAddressField.PAYER_ADDRESS_FIELD)
          .querySelector("input[data-city]"),
      );
      list.set(
        EAddressField.PAYER_COUNTRY,
        this.formElement.querySelector("#input-field-payerAddress-countryCode"),
      );
      list.set(
        EAddressField.PAYER_SUGGESTIONS,
        list
          .get(EAddressField.PAYER_ADDRESS_FIELD)
          .querySelector("div[data-suggestions]"),
      );
      list.set(
        EAddressField.PAYER_SUGGESTION_LIST,
        list
          .get(EAddressField.PAYER_ADDRESS_FIELD)
          .querySelector(".suggestion-list"),
      );
      list.set(
        EAddressField.PAYER_CLOSE_BUTTON,
        list
          .get(EAddressField.PAYER_ADDRESS_FIELD)
          .querySelector(".suggestion-close-button"),
      );
    }

    this.elements = list;
  }

  protected addEvents() {
    this.documentClickEvent$ = fromEvent(document, "click");

    fromEvent(
      <HTMLDivElement>this.elements.get(EAddressField.MAIN_CLOSE_BUTTON),
      "click",
    ).subscribe(() =>
      (
        this.elements.get(EAddressField.MAIN_SUGGESTIONS) as HTMLDivElement
      ).classList.add("hidden"),
    );

    fromEvent(
      <HTMLDivElement>this.elements.get(EAddressField.DELIVERY_CLOSE_BUTTON),
      "click",
    ).subscribe(() =>
      (
        this.elements.get(EAddressField.DELIVERY_SUGGESTIONS) as HTMLDivElement
      ).classList.add("hidden"),
    );

    fromEvent(
      <HTMLDivElement>this.elements.get(EAddressField.BILLING_CLOSE_BUTTON),
      "click",
    ).subscribe(() =>
      (
        this.elements.get(EAddressField.BILLING_SUGGESTIONS) as HTMLDivElement
      ).classList.add("hidden"),
    );

    fromEvent(
      <HTMLDivElement>this.elements.get(EAddressField.PAYER_CLOSE_BUTTON),
      "click",
    ).subscribe(() =>
      (
        this.elements.get(EAddressField.PAYER_SUGGESTIONS) as HTMLDivElement
      ).classList.add("hidden"),
    );

    const mainAddressEventConfig = new ZipCityAutocompletionEventConfig();
    mainAddressEventConfig.name = "mainAddress";
    mainAddressEventConfig.countryField = this.elements.get(
      EAddressField.MAIN_COUNTRY,
    ) as HTMLInputElement;
    mainAddressEventConfig.postalCodeField = this.elements.get(
      EAddressField.MAIN_POSTAL_CODE,
    ) as HTMLInputElement;
    mainAddressEventConfig.cityField = this.elements.get(
      EAddressField.MAIN_CITY,
    ) as HTMLInputElement;
    mainAddressEventConfig.suggestionElement = this.elements.get(
      EAddressField.MAIN_SUGGESTIONS,
    ) as HTMLInputElement;
    mainAddressEventConfig.suggestionListElement = this.elements.get(
      EAddressField.MAIN_SUGGESTION_LIST,
    ) as HTMLInputElement;

    const deliveryAddressEventConfig = new ZipCityAutocompletionEventConfig();
    deliveryAddressEventConfig.name = "deliveryAddress";
    deliveryAddressEventConfig.countryField = this.elements.get(
      EAddressField.DELIVERY_COUNTRY,
    ) as HTMLInputElement;
    deliveryAddressEventConfig.postalCodeField = this.elements.get(
      EAddressField.DELIVERY_POSTAL_CODE,
    ) as HTMLInputElement;
    deliveryAddressEventConfig.cityField = this.elements.get(
      EAddressField.DELIVERY_CITY,
    ) as HTMLInputElement;
    deliveryAddressEventConfig.suggestionElement = this.elements.get(
      EAddressField.DELIVERY_SUGGESTIONS,
    ) as HTMLInputElement;
    deliveryAddressEventConfig.suggestionListElement = this.elements.get(
      EAddressField.DELIVERY_SUGGESTION_LIST,
    ) as HTMLInputElement;

    const billingAddressEventConfig = new ZipCityAutocompletionEventConfig();
    billingAddressEventConfig.name = "billingAddress";
    billingAddressEventConfig.countryField = this.elements.get(
      EAddressField.BILLING_COUNTRY,
    ) as HTMLInputElement;
    billingAddressEventConfig.postalCodeField = this.elements.get(
      EAddressField.BILLING_POSTAL_CODE,
    ) as HTMLInputElement;
    billingAddressEventConfig.cityField = this.elements.get(
      EAddressField.BILLING_CITY,
    ) as HTMLInputElement;
    billingAddressEventConfig.suggestionElement = this.elements.get(
      EAddressField.BILLING_SUGGESTIONS,
    ) as HTMLInputElement;
    billingAddressEventConfig.suggestionListElement = this.elements.get(
      EAddressField.BILLING_SUGGESTION_LIST,
    ) as HTMLInputElement;

    const payerAddressEventConfig = new ZipCityAutocompletionEventConfig();
    payerAddressEventConfig.name = "billingAddress";
    payerAddressEventConfig.countryField = this.elements.get(
      EAddressField.PAYER_COUNTRY,
    ) as HTMLInputElement;
    payerAddressEventConfig.postalCodeField = this.elements.get(
      EAddressField.PAYER_POSTAL_CODE,
    ) as HTMLInputElement;
    payerAddressEventConfig.cityField = this.elements.get(
      EAddressField.PAYER_CITY,
    ) as HTMLInputElement;
    payerAddressEventConfig.suggestionElement = this.elements.get(
      EAddressField.PAYER_SUGGESTIONS,
    ) as HTMLInputElement;
    payerAddressEventConfig.suggestionListElement = this.elements.get(
      EAddressField.PAYER_SUGGESTION_LIST,
    ) as HTMLInputElement;

    const eventConfig = [
      mainAddressEventConfig,
      deliveryAddressEventConfig,
      billingAddressEventConfig,
      payerAddressEventConfig,
    ];

    for (const config of eventConfig) {
      this.setInputAutocompleteValue(
        config.postalCodeField,
        this.getBrowserAutoCompletionOffValue(),
      );
      this.setInputAutocompleteValue(
        config.cityField,
        this.getBrowserAutoCompletionOffValue(),
      );

      // Complete city with postal code
      fromEvent(config.postalCodeField, "keyup")
        .pipe(
          distinctUntilChanged(),
          debounceTime(200),
          map((event) => (event.target as HTMLInputElement).value),
          filter((zipcode) => zipcode.length >= 3),
          switchMap((zipcode) => {
            const countryValue = config?.countryField?.value || "DE";
            return ZipCityAutocompletionRepo.getCitiesByZipCode(
              countryValue,
              zipcode,
            ).pipe(catchError(() => []));
          }),
          filter((result) => result.length > 0),
          tap((result) => this.makeSuggestion(result, config)),
        )
        .subscribe();
    }
  }

  protected makeSuggestion(
    result: ZipCityAutocompletionResponseModel[],
    config: ZipCityAutocompletionEventConfig,
  ) {
    if (result[0]) {
      this.addSuggestions(result[0], config);
    } else {
      config.suggestionElement.classList.remove("hidden");
      config.suggestionListElement.innerText = "";

      const uniqueCities = result.filter((city, index) => {
        return (
          index ===
          result.findIndex((city2) => city.placeName === city2.placeName)
        );
      });

      for (const city of uniqueCities) {
        const suggestion = document.createElement("div");
        suggestion.innerHTML = DomService.removeScriptsFromString(
          city.placeName,
        );

        config.suggestionListElement.append(suggestion);

        fromEvent(suggestion, "click")
          .pipe(take(1))
          .subscribe(() => this.addSuggestions(city, config));
      }
    }
  }

  protected addSuggestions(
    city: ZipCityAutocompletionResponseModel,
    config: ZipCityAutocompletionEventConfig,
  ) {
    config.cityField.value = city.placeName;
    config.suggestionListElement.innerText = "";
    config.suggestionElement.classList.add("hidden");
  }

  protected setInputAutocompleteValue(field: HTMLInputElement, value: string) {
    if (!field) return;

    field.setAttribute("autocomplete", value);
  }

  protected getBrowserAutoCompletionOffValue() {
    const agent = navigator.userAgent;

    if (agent.match(/chrome|chromium|crios/i)) {
      //Chrome/Chromium
      return "none";
    } else if (agent.match(/firefox|fxios/i)) {
      // Firefox
      return "off";
    } else {
      return "off";
    }
  }
}
